import type {
    PaymentBillableConnectorsSummary,
    PaymentProductDisplayResponse,
    PaymentProductPriceDisplay,
    PaymentProductsDisplayResponse,
} from '../../../common/redux/api/web-api'
import { SWEDISH_CURRENCY } from '../../../common/services/utilsService'
import type { SubscriptionMarketingFeature } from '../subscription-card-layout/subscription-card-layout'
import type { BillingInterval } from '../subscription-card-widget/subscription-card-widget-model'

const BASIC_MARKETING_FEATURES: SubscriptionMarketingFeature[] = [
    { label: 'lblBasicProductFeatureLine02', isActive: false, productLevel: 0 },
    { label: 'lblBasicProductFeatureLine01', isActive: false, productLevel: 0 },
    { label: 'lblBasicProductFeatureLine03', isActive: false, productLevel: 0 },
    { label: 'lblBasicProductFeatureLine04', isActive: false, productLevel: 0 },
    { label: 'lblBasicProductFeatureLine05', isActive: false, productLevel: 0 },
    { label: 'lblBasicProductFeatureLine06', isActive: false, productLevel: 0 },
    { label: 'lblBasicProductFeatureLine07', isActive: false, productLevel: 0 },
]

export type SelectedSubscriptionOffering = {
    product: PaymentProductDisplayResponse
    price: PaymentProductPriceDisplay
}

export const getCommercialOfferingsCurrency = (commercialOfferings: PaymentProductsDisplayResponse): string => {
    const productCurrency = commercialOfferings.products
        ?.flatMap((product) => product.prices ?? [])
        .find((price) => price.currency?.trim())
        ?.currency?.trim()

    return productCurrency || commercialOfferings.administrativeFee?.currency?.trim() || SWEDISH_CURRENCY
}

export const getProductPrice = (
    product: PaymentProductDisplayResponse,
    billingInterval: BillingInterval
): PaymentProductPriceDisplay => {
    const price = product.prices?.find((candidate) => candidate.interval === billingInterval)

    if (!price) {
        throw new Error(`Missing ${billingInterval} price for ${product.name} subscription`)
    }

    return price
}

export const getBillableConnectorQuantity = (
    billableConnectors: PaymentBillableConnectorsSummary | null | undefined,
    product: PaymentProductDisplayResponse
): number =>
    billableConnectors?.quantities?.find((quantity) => quantity.strategyKey === product.quantityStrategyKey)
        ?.quantity ?? 0

export const supportsInvoiceDiscountCode = ({ product, price }: SelectedSubscriptionOffering): boolean => {
    const productName = product.name.trim().toLowerCase()

    return productName === 'flex' || (productName === 'pro' && price.interval === 'month')
}

export const buildSubscriptionMarketingFeatures = (
    products: PaymentProductDisplayResponse[] | undefined
): SubscriptionMarketingFeature[] => {
    const productMarketingFeatures = (products ?? []).flatMap((product) =>
        (product.marketingFeatures ?? []).map((label) => ({
            label,
            isActive: false,
            productLevel: product.productLevel ?? 0,
        }))
    )

    return Array.from(
        new Map(
            [...BASIC_MARKETING_FEATURES, ...productMarketingFeatures].map((feature) => [feature.label, feature])
        ).values()
    )
}

export const toStripeLocale = (locale?: string | null): string | undefined => {
    const normalizedLocale = locale?.trim().toLowerCase()

    if (!normalizedLocale) return undefined

    return normalizedLocale === 'no' ? 'nb' : normalizedLocale
}
